import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ProduccionB2bShell } from "@/components/b2b/ProduccionB2bShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable, CrudEmptyState, Panel } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/produccion/usuarios")({
  component: ProduccionUsuariosPage,
  head: () => ({ meta: [{ title: "Usuarios — B2B / Mayorista" }] }),
});

interface InstitutionalUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  institutionName: string | null;
  channelId: string | null;
  channelName: string | null;
  status: string;
  creditLimit: number | null;
  creditUsed: number;
}

interface B2bChannelOption {
  id: string;
  name: string;
}

function ProduccionUsuariosPage() {
  const tid = useWorkspaceTenantId();
  const [users, setUsers] = useState<InstitutionalUser[]>([]);
  const [b2bChannels, setB2bChannels] = useState<B2bChannelOption[]>([]);
  const [approveChannel, setApproveChannel] = useState<Record<string, string>>({});
  const [creditDraft, setCreditDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!tid) return;
    try {
      const [u, ch] = await Promise.all([
        apiGetJson<InstitutionalUser[]>("/api/institutional/users", tid),
        apiGetJson<B2bChannelOption[]>("/api/b2b/channels", tid),
      ]);
      setUsers(u);
      setB2bChannels(ch);
      const drafts: Record<string, string> = {};
      for (const row of u) {
        drafts[row.id] = row.creditLimit == null ? "" : String(row.creditLimit);
      }
      setCreditDraft(drafts);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar usuarios");
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const approveUser = async (userId: string, channelId?: string | null) => {
    if (!tid) return;
    try {
      await apiSendJson(
        "/api/institutional/users",
        "PATCH",
        { userId, action: "approve", channelId: channelId ?? null },
        tid,
      );
      toast.success("Usuario aprobado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const rejectUser = async (userId: string) => {
    if (!tid) return;
    try {
      await apiSendJson("/api/institutional/users", "PATCH", { userId, action: "reject" }, tid);
      toast.success("Usuario rechazado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const saveCredit = async (userId: string) => {
    if (!tid) return;
    const raw = (creditDraft[userId] ?? "").trim();
    const creditLimit = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isFinite(creditLimit) || (creditLimit as number) < 0)) {
      toast.error("Límite inválido");
      return;
    }
    try {
      await apiSendJson(
        "/api/institutional/users",
        "PATCH",
        { userId, action: "set_credit", creditLimit },
        tid,
      );
      toast.success(creditLimit == null ? "Sin tope de crédito" : `Límite $${creditLimit}`);
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const columns: DataTableColumn<InstitutionalUser>[] = [
    {
      key: "usuario",
      header: "Usuario",
      render: (u) => (
        <div>
          <p className="font-medium">
            {u.firstName} {u.lastName}
          </p>
          <p className="text-xs text-muted-foreground">{u.email}</p>
        </div>
      ),
    },
    { key: "institucion", header: "Institución", render: (u) => u.institutionName ?? "—" },
    {
      key: "canal",
      header: "Canal B2B",
      render: (u) =>
        u.status === "pending" ? (
          <Select
            value={approveChannel[u.id] ?? u.channelId ?? "_none"}
            onValueChange={(v) => setApproveChannel((prev) => ({ ...prev, [u.id]: v }))}
          >
            <SelectTrigger className="h-8 w-[180px]">
              <SelectValue placeholder="Asignar canal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Sin canal (asignar después)</SelectItem>
              {b2bChannels.map((ch) => (
                <SelectItem key={ch.id} value={ch.id}>
                  {ch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-sm">{u.channelName ?? "—"}</span>
        ),
    },
    {
      key: "credito",
      header: "Cuenta corriente",
      render: (u) => (
        <div className="space-y-1 min-w-[160px]">
          <div className="flex items-center gap-1">
            <Input
              className="h-8 w-[110px]"
              inputMode="decimal"
              placeholder="Sin tope"
              value={creditDraft[u.id] ?? ""}
              onChange={(e) => setCreditDraft((prev) => ({ ...prev, [u.id]: e.target.value }))}
              disabled={u.status !== "approved"}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={u.status !== "approved"}
              onClick={() => void saveCredit(u.id)}
            >
              OK
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Usado: ${(u.creditUsed ?? 0).toLocaleString("es-AR")}
            {u.creditLimit != null
              ? ` · Tope: $${u.creditLimit.toLocaleString("es-AR")}`
              : " · Sin tope"}
          </p>
        </div>
      ),
    },
    {
      key: "estado",
      header: "Estado",
      render: (u) => (
        <Badge variant={u.status === "approved" ? "default" : u.status === "pending" ? "secondary" : "outline"}>
          {u.status}
        </Badge>
      ),
    },
    {
      key: "acciones",
      header: "Acciones",
      className: "text-right",
      render: (u) =>
        u.status === "pending" ? (
          <div className="space-x-1">
            <Button
              size="sm"
              onClick={() => {
                const ch = approveChannel[u.id];
                void approveUser(u.id, ch && ch !== "_none" ? ch : u.channelId);
              }}
            >
              Aprobar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void rejectUser(u.id)}>
              Rechazar
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <ProduccionB2bShell title="Usuarios institucionales">
      <Panel title="Usuarios institucionales">
        <DataTable
          columns={columns}
          rows={users}
          getRowKey={(u) => u.id}
          emptyState={<CrudEmptyState title="Sin usuarios institucionales" />}
        />
      </Panel>
    </ProduccionB2bShell>
  );
}
